import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BottomNavigation from '../components/BottomNavigation';
import { ArrowLeft, Globe, Bell, BellRing, Shield, HelpCircle, Info, ChevronRight, Mail, Phone, MessageCircle, Trash2, ChevronDown, Share2, LogOut } from 'lucide-react';
import usePricingConfig from '../hooks/usePricingConfig';
import AppBadgeToggle from '../components/AppBadgeToggle';
import { useAuth } from '../App';
import { shareFind2Day } from '../utils/shareApp';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'de', label: 'Deutsch' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'fr', label: 'Français' },
  { code: 'ar', label: 'العربية' },
];

const Settings = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { logout } = useAuth();
  const pricing = usePricingConfig();

  const handleLogout = () => {
    logout();
    navigate('/');
  };
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showLangPicker, setShowLangPicker] = useState(false);

  // Deep-link: /settings?help=1 auto-opens the existing Help Center modal.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('help') === '1') setShowHelpModal(true);
  }, []);

  // Notification preferences (from device_subscriptions)
  const [notifPrefs, setNotifPrefs] = useState({
    notificationsEnabled: true,
    emailEnabled: false,
    hasPush: false,
    featuredContentEnabled: false,
  });
  const [notifLoading, setNotifLoading] = useState(true);

  const subId = localStorage.getItem('find2day_sub_id') || localStorage.getItem('subscriptionId') || '';

  useEffect(() => {
    if (subId) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/preferences/${subId}`)
        .then(r => r.json())
        .then(data => {
          setNotifPrefs({
            notificationsEnabled: data.notificationsEnabled ?? true,
            emailEnabled: data.emailEnabled ?? false,
            hasPush: data.hasPush ?? false,
            featuredContentEnabled: data.featuredContentEnabled ?? false,
          });
          setNotifLoading(false);
        })
        .catch(() => setNotifLoading(false));
    } else {
      setNotifLoading(false);
    }
  }, [subId]);

  const updateNotifPref = async (key, value) => {
    const newPrefs = { ...notifPrefs, [key]: value };
    setNotifPrefs(newPrefs);
    if (subId) {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/update-preferences`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subscriptionId: subId, [key]: value }),
        });
      } catch (e) {
        console.error('Failed to update preference:', e);
      }
    }
  };

  const currentLang = i18n.language?.split('-')[0] || 'en';
  const currentLangLabel = LANGUAGES.find(l => l.code === currentLang)?.label || 'English';

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
    setShowLangPicker(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/profile')}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5 rtl-flip" />
            </button>
            <h1 className="text-xl font-bold" data-testid="settings-title">{t('settings', 'Settings')}</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Preferences */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b">
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
              {t('preferences', 'Preferences')}
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {/* Language */}
            <div className="relative">
              <button
                onClick={() => setShowLangPicker(!showLangPicker)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                data-testid="setting-language"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Globe className="h-5 w-5 text-purple-600" />
                  </div>
                  <span className="text-gray-900 font-medium">{t('language', 'Language')}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <span className="text-sm">{currentLangLabel}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${showLangPicker ? 'rotate-180' : ''}`} />
                </div>
              </button>
              {showLangPicker && (
                <div className="border-t border-gray-100 bg-gray-50">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full flex items-center justify-between px-4 py-3 text-sm transition-colors ${
                        currentLang === lang.code ? 'bg-purple-50 text-purple-700 font-semibold' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      data-testid={`settings-lang-${lang.code}`}
                    >
                      <span className="ml-12">{lang.label}</span>
                      {currentLang === lang.code && <span className="text-purple-600 text-xs font-bold">&#10003;</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Notifications Section */}
            <div className="px-4 py-3 bg-gray-50 border-b border-t">
              <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                {t('notificationSettings', 'Notification Settings')}
              </h2>
            </div>

            {/* Iter 210 — entry point to the full notification preferences page */}
            <div className="px-4 py-3 bg-violet-50 border-b border-violet-100">
              <button
                type="button"
                onClick={() => navigate('/preferences')}
                className="w-full flex items-center justify-between text-left p-2 rounded-lg hover:bg-white transition-colors"
                data-testid="open-notification-preferences"
              >
                <div>
                  <p className="text-sm font-semibold text-violet-900">New notification preferences</p>
                  <p className="text-xs text-violet-700 mt-0.5">Choose categories, add backup email, and turn on email alerts for critical safety notices.</p>
                </div>
                <span className="text-violet-700 text-lg">›</span>
              </button>
            </div>

            {/* App icon badge / phone alerts (Web Push + Badging API).
                Restored REG-009 — shared with the public inbox page. */}
            <div className="p-4 border-b">
              <AppBadgeToggle />
            </div>

            {notifLoading ? (
              <div className="p-4 text-center text-gray-400 text-sm">Loading...</div>
            ) : !subId ? (
              <div className="p-4">
                <p className="text-sm text-gray-500">{t('enableNotificationsFirst', 'Follow a business or enable notifications to manage your preferences.')}</p>
                <p className="text-xs text-gray-400 mt-3">
                  {t('nearbyDealsMovedToDiscover', 'Nearby Deals & Promotions have moved — manage them from the')} <button onClick={() => navigate('/discover')} className="text-purple-600 underline" data-testid="nearby-deals-link-discover">{t('discoverPage', 'Discover page')}</button>.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {/* Push Notifications */}
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="p-2 bg-purple-100 rounded-lg"><Bell className="h-5 w-5 text-purple-600" /></div>
                    <div>
                      <span className="text-gray-900 font-medium block text-sm">{t('pushNotifications', 'Push Notifications')}</span>
                      <span className="text-xs text-gray-500">{t('pushDesc', 'Receive alerts about offers and events')}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => updateNotifPref('hasPush', !notifPrefs.hasPush)}
                    className={`w-12 h-6 rounded-full transition-colors flex-shrink-0 ${notifPrefs.hasPush ? 'bg-purple-600' : 'bg-gray-300'}`}
                    data-testid="toggle-push"
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform mt-0.5 ${notifPrefs.hasPush ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                {/* Email Notifications */}
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="p-2 bg-purple-100 rounded-lg"><Mail className="h-5 w-5 text-purple-600" /></div>
                    <div>
                      <span className="text-gray-900 font-medium block text-sm">{t('emailNotifications', 'Email Notifications')}</span>
                      <span className="text-xs text-gray-500">{t('emailDesc', 'Receive offers, events and updates by email')}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => updateNotifPref('emailEnabled', !notifPrefs.emailEnabled)}
                    className={`w-12 h-6 rounded-full transition-colors flex-shrink-0 ${notifPrefs.emailEnabled ? 'bg-purple-600' : 'bg-gray-300'}`}
                    data-testid="toggle-email"
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform mt-0.5 ${notifPrefs.emailEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                <div className="p-4 text-xs text-gray-400">
                  {t('nearbyDealsMovedToDiscover', 'Nearby Deals & Promotions have moved — manage them from the')} <button onClick={() => navigate('/discover')} className="text-purple-600 underline" data-testid="nearby-deals-link-discover-2">{t('discoverPage', 'Discover page')}</button>.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Account */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b">
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
              {t('account', 'Account')}
            </h2>
          </div>
          <button
            onClick={() => navigate('/change-password')}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            data-testid="setting-privacy"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="h-5 w-5 text-purple-600" />
              </div>
              <span className="text-gray-900 font-medium">{t('privacySecurity', 'Privacy & Security')}</span>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-500 rtl-flip" />
          </button>
        </div>

        {/* Support */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b">
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
              {t('support', 'Support')}
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            <button
              onClick={() => setShowHelpModal(true)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              data-testid="setting-help"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <HelpCircle className="h-5 w-5 text-purple-600" />
                </div>
                <span className="text-gray-900 font-medium">{t('helpCenter', 'Help Center')}</span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-500 rtl-flip" />
            </button>

            <button
              onClick={() => shareFind2Day(t)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              data-testid="setting-share-app"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Share2 className="h-5 w-5 text-purple-600" />
                </div>
                <span className="text-gray-900 font-medium">{t('shareApp', 'Share Find-2-Day')}</span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-500 rtl-flip" />
            </button>

            <button
              onClick={() => setShowAboutModal(true)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              data-testid="setting-about"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Info className="h-5 w-5 text-purple-600" />
                </div>
                <span className="text-gray-900 font-medium">{t('aboutApp', 'About Find-2-Day')}</span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-500 rtl-flip" />
            </button>
          </div>
        </div>

        {/* Legal */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b">
            <h2 className="font-semibold text-gray-900">{t('legal', 'Legal')}</h2>
          </div>
          <div className="divide-y">
            <button onClick={() => navigate('/terms')} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors" data-testid="setting-terms">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg"><Shield className="h-5 w-5 text-gray-600" /></div>
                <span className="text-gray-900 font-medium">{t('termsOfService')}</span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-500 rtl-flip" />
            </button>
            <button onClick={() => navigate('/privacy')} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors" data-testid="setting-privacy">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg"><Shield className="h-5 w-5 text-gray-600" /></div>
                <span className="text-gray-900 font-medium">{t('privacyPolicy')}</span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-500 rtl-flip" />
            </button>
            <button onClick={() => navigate('/cookies')} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors" data-testid="setting-cookies">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg"><Shield className="h-5 w-5 text-gray-600" /></div>
                <span className="text-gray-900 font-medium">{t('cookiePolicy')}</span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-500 rtl-flip" />
            </button>

            <button
              onClick={() => navigate('/data-deletion')}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              data-testid="setting-data-deletion"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <span className="text-gray-900 font-medium">{t('deleteAccountTitle')}</span>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-500 rtl-flip" />
            </button>
          </div>
        </div>

        {/* Log Out */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 p-4 text-red-600 hover:bg-red-50 transition-colors"
            data-testid="setting-logout"
          >
            <div className="p-2 bg-red-100 rounded-lg">
              <LogOut className="h-5 w-5 text-red-600" />
            </div>
            <span className="font-semibold">{t('logout', 'Log Out')}</span>
          </button>
        </div>

        {/* App version */}
        <div className="text-center text-sm text-gray-400 pt-4">
          Find-2-Day v1.0
        </div>
      </div>

      {/* Help Center Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">{t('helpCenter', 'Help Center')}</h2>
                <button onClick={() => setShowHelpModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6 overflow-y-auto">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">{t('faqTitle', 'Frequently Asked Questions')}</h3>
                <div className="space-y-3">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium text-gray-900">{t('faq1q', 'How do I register my business?')}</p>
                    <p className="text-sm text-gray-600 mt-1">{t('faq1a', 'Click "Join as a Business" on the home page and follow the 5-step registration process.')}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium text-gray-900">{t('faq2q', 'How does billing work?')}</p>
                    <p className="text-sm text-gray-600 mt-1">{`${pricing.foundingTitle} — ${pricing.foundingNoCardRequired ? 'no card required, no payment during the founding period.' : ''} ${pricing.foundingFuturePricingNotice}`}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium text-gray-900">{t('faq3q', 'When will my business appear?')}</p>
                    <p className="text-sm text-gray-600 mt-1">{t('faq3a', 'Your business will be visible after admin approval, usually within 24-48 hours.')}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">{t('contactUs', 'Contact Us')}</h3>
                <div className="space-y-2">
                  <a href="mailto:find-2-day@outlook.com" className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg text-purple-700 hover:bg-purple-100 transition-colors">
                    <Mail className="h-5 w-5" />
                    <span>find-2-day@outlook.com</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* About Modal */}
      {showAboutModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">{t('aboutApp', 'About Find-2-Day')}</h2>
                <button onClick={() => setShowAboutModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-center">
                <div className="w-20 h-20 rounded-2xl overflow-hidden mx-auto mb-4 shadow-sm border border-purple-100 bg-white flex items-center justify-center">
                  <img
                    src="/logo.jpg"
                    alt="Find-2-Day"
                    className="w-full h-full object-cover"
                    data-testid="about-app-logo"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Find-2-Day</h3>
                <p className="text-gray-500">Version 1.0</p>
              </div>

              <p className="text-gray-600 text-center">
                {t('aboutDescription', 'Find-2-Day is your local discovery platform. Find restaurants, events, activities, and special offers from businesses near you.')}
              </p>

              <div className="pt-4 border-t space-y-2 text-sm text-gray-500">
                <p className="flex justify-between">
                  <span>{t('subscriptionPlans', 'Subscription Plans')}:</span>
                  <span className="font-medium text-gray-900">{pricing.foundingTitle}</span>
                </p>
              </div>

              <div className="pt-4 text-center text-xs text-gray-400">
                © 2025 Find-2-Day. {t('allRightsReserved', 'All rights reserved.')}
              </div>
            </div>
          </div>
        </div>
      )}

      <BottomNavigation />
    </div>
  );
};

export default Settings;
